import { useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuthStore } from '../../store/useAuthStore';
import { useLeaderboard, useMyStats } from '../../hooks/useLeaderboard';
import { useCourses } from '../../hooks/useCourses';
import { useLabs, useMyLabProgress } from '../../hooks/useLabs';
import { useMyAssignments } from '../../hooks/useGradebook';
import CITADEL_CSS from './citadel.css?raw';
import CITADEL_HTML from './citadel.body.html?raw';
import citadelLogo from '../../assets/brand/cyberelites-mark.png';

// The Citadel's rank ladder is purely point-threshold driven, so it maps 1:1 to
// real points — the tier names are data, not a missing backend feature.
const TIERS = [
  { n: 'Recruit', min: 0, role: 'Onboarding' },
  { n: 'Lookout', min: 500, role: 'SOC trainee' },
  { n: 'Sentinel', min: 1500, role: 'SOC Analyst L1' },
  { n: 'Warden', min: 3000, role: 'SOC Analyst L2' },
  { n: 'Castellan', min: 5000, role: 'Threat Hunter' },
  { n: 'Marshal', min: 8000, role: 'IR lead' },
] as const;
const tierIndex = (pts: number) => { let i = 0; TIERS.forEach((t, k) => { if (pts >= t.min) i = k; }); return i; };
const diffClass = (d?: string) => (/hard/i.test(d ?? '') ? 'hard' : /easy|beginner/i.test(d ?? '') ? 'easy' : 'med');
const titleize = (s: string) => s.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const cap = (s?: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

/**
 * The Citadel Console — the approved 3D trainee experience, ported per
 * CITADEL-DESIGN-HANDOFF.md (CSS, markup, fonts and the three.js module kept
 * intact) with real data wired in. Shown to trainees whose org superadmin
 * picked the 'fortress' template. Full-bleed over the app; styles namespaced
 * under `.citadel-root`.
 *
 * Every card, beacon and door drives the SAME real routes the default template
 * uses: a lab card navigates to `/labs/$id` (which starts + opens the VM) and a
 * course card to `/courses/$id`. Navigating there unmounts this overlay, so the
 * trainee lands on the real, working page — the Citadel is the front door, not
 * a parallel fake.
 */

const initials = (s: string) =>
  (s || 'OP').split(/[\s.@_-]+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || 'OP';
const esc = (s: string) =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] ?? c));

interface CitLab { slug: string; title: string; category: string; difficulty?: string | undefined; points: number; }
interface CitCourse { slug: string; title: string; difficulty?: string; moduleCount?: number; estimatedHours?: number; enrolled?: boolean; }
interface CitProg { completed: boolean; percentComplete: number; status: string; }

// The board/course grids are DATA-DRIVEN, not fixed slots — we render one card
// per real item (so nothing is cut off, faked, or left empty-with-stale-content).
// Each card reuses a 3D prop icon published by the module on window.__CITADEL_PROPS__.
const LAB_PROPS = ['key', 'hourglass', 'gears', 'scroll', 'lantern', 'flask', 'spyglass', 'brazier', 'chest', 'compass'];
const COURSE_PROPS = ['tome', 'brazier', 'scroll', 'compass', 'hourglass', 'lantern', 'gears', 'flask'];

function labStatusHtml(p?: CitProg): string {
  const done = !!p?.completed;
  const running = !!p && !p.completed && (p.percentComplete > 0 || p.status === 'running' || p.status === 'ready');
  const dot = done ? 'done' : running ? 'live' : '';
  const txt = done ? 'Cleared · 100%' : running ? `In progress · ${Math.round(p!.percentComplete)}%` : 'Assigned';
  return `<span class="sdot ${dot}"></span>${txt}`;
}
function labCardHtml(lab: CitLab, i: number, p?: CitProg): string {
  return `<button class="rcard" type="button" data-lab-slug="${esc(lab.slug)}">`
    + `<img class="prop" data-prop="${LAB_PROPS[i % LAB_PROPS.length]}" alt="">`
    + `<div class="status">${labStatusHtml(p)}</div>`
    + `<h3>${esc(lab.title)}</h3>`
    + `<div class="tn">${esc(lab.category)}</div>`
    + `<div class="foot"><span class="diff ${diffClass(lab.difficulty)}">${esc(cap(lab.difficulty) || 'Lab')}</span>`
    + `<span class="xp">+${lab.points.toLocaleString()} XP</span></div></button>`;
}
function courseCardHtml(c: CitCourse, i: number): string {
  const status = c.enrolled ? 'Enrolled' : 'Available';
  const btn = c.enrolled ? 'Continue' : 'Start';
  return `<button class="wcard" type="button" data-course-slug="${esc(c.slug)}">`
    + `<img class="prop" data-prop="${COURSE_PROPS[i % COURSE_PROPS.length]}" alt="">`
    + `<div class="ccat">${esc(cap(c.difficulty) || 'Course')}</div>`
    + `<h3>${esc(c.title)}</h3>`
    + `<div class="cmeta">${c.moduleCount ?? 0} modules${c.estimatedHours ? ` · ~${c.estimatedHours}h` : ''}</div>`
    + `<div class="foot"><span class="xp">${status}</span><span class="btn primary sm">${btn}</span></div></button>`;
}
const emptyNote = (msg: string): string =>
  `<div class="cit-empty" style="grid-column:1/-1;padding:28px 22px;border:1px dashed rgba(255,255,255,.14);border-radius:14px;color:var(--faint,#8aa0b5);text-align:center;font-size:13.5px;background:rgba(255,255,255,.02)">${esc(msg)}</div>`;
function applyCitadelProps(root: HTMLElement): void {
  const store = (window as unknown as { __CITADEL_PROPS__?: Record<string, string> }).__CITADEL_PROPS__ || {};
  root.querySelectorAll<HTMLImageElement>('img.prop[data-prop]').forEach((im) => {
    if (im.getAttribute('src')) return;
    const name = im.getAttribute('data-prop');
    if (name && store[name]) im.src = store[name];
  });
}

export function Citadel() {
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const stats = useMyStats().data;
  const board = useLeaderboard().data;
  const courses = useCourses().data;
  const labs = useLabs().data;
  const labProg = useMyLabProgress().data;
  const assignments = useMyAssignments().data;

  // ---- mount: fonts, router, real logo, click→real-route wiring, 3D module ----
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const fontId = 'citadel-fonts';
    if (!document.getElementById(fontId)) {
      const link = document.createElement('link');
      link.id = fontId;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700&family=Manrope:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600&display=swap';
      document.head.appendChild(link);
    }

    // Real CyberElites logo in the brand mark (replaces the traced SVG).
    const mark = root.querySelector<HTMLElement>('.brand .mark');
    if (mark) {
      mark.style.background = 'transparent';
      mark.style.border = 'none';
      mark.innerHTML = `<img src="${citadelLogo}" alt="CyberElites" style="width:100%;height:100%;object-fit:contain">`;
    }

    // Router — view switching for the console/board views, BUT any element that
    // carries a real lab or course slug leaves the Citadel and opens the real
    // page (same as the default template). This is what makes labs actually work.
    const views: Record<string, HTMLElement> = {};
    root.querySelectorAll<HTMLElement>('.view').forEach((v) => { const k = v.getAttribute('data-view'); if (k) views[k] = v; });
    const navlinks = root.querySelectorAll<HTMLElement>('.nav a');
    const NAVKEY: Record<string, string> = { console: 'console', learn: 'learn', lesson: 'learn', range: 'range', mission: 'range', rank: 'rank' };
    const show = (name: string) => {
      if (!views[name]) name = 'console';
      Object.keys(views).forEach((k) => { views[k]!.hidden = k !== name; });
      navlinks.forEach((a) => a.classList.toggle('on', a.getAttribute('data-go') === NAVKEY[name]));
      root.scrollTo?.(0, 0);
      window.dispatchEvent(new CustomEvent('viewchange', { detail: name }));
    };
    const openLab = (slug: string) => navigate({ to: '/labs/$id', params: { id: slug } });
    const openCourse = (slug: string) => navigate({ to: '/courses/$id', params: { id: slug } });
    const onClick = (e: Event) => {
      const target = e.target as HTMLElement;
      // 1. A card/beacon tagged with a real lab → open the real lab page.
      const labEl = target.closest<HTMLElement>('[data-lab-slug]');
      const labSlug = labEl?.getAttribute('data-lab-slug');
      if (labSlug) { e.preventDefault(); openLab(labSlug); return; }
      // 2. A card tagged with a real course → open the real course page.
      const courseEl = target.closest<HTMLElement>('[data-course-slug]');
      const courseSlug = courseEl?.getAttribute('data-course-slug');
      if (courseSlug) { e.preventDefault(); openCourse(courseSlug); return; }
      // 3. Otherwise it's navigation. 'mission'/'lesson' are the design's inline
      //    detail views — we never show those fakes; route to the real current
      //    lab/course if one is known, else fall back to the live board.
      const goEl = target.closest<HTMLElement>('[data-go]');
      if (goEl) {
        e.preventDefault();
        const go = goEl.getAttribute('data-go') || 'console';
        if (go === 'mission') { const s = root.dataset.curLab; if (s) openLab(s); else show('range'); return; }
        if (go === 'lesson') { const s = root.dataset.curCourse; if (s) openCourse(s); else show('learn'); return; }
        show(go);
        return;
      }
      const chip = target.closest('.fchip');
      if (chip) { chip.parentElement?.querySelectorAll('.fchip').forEach((x) => x.classList.toggle('on', x === chip)); }
    };
    root.addEventListener('click', onClick);

    // When the 3D module finishes rendering the prop icons, paint them onto any
    // cards we built dynamically (they may exist before the icons are ready).
    const onProps = () => applyCitadelProps(root);
    window.addEventListener('citadelprops', onProps);

    let cancelled = false;
    import('./citadel3d').catch(() => { /* WebGL unavailable → CSS fallback */ });
    requestAnimationFrame(() => { if (!cancelled) window.dispatchEvent(new CustomEvent('viewchange', { detail: 'console' })); });

    return () => {
      cancelled = true;
      root.removeEventListener('click', onClick);
      window.removeEventListener('citadelprops', onProps);
    };
  }, [navigate]);

  // ---- wire ALL real data in (text/attrs only; markup + classes intact) ----
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const setText = (sel: string, v: string) => root.querySelectorAll<HTMLElement>(sel).forEach((el) => { el.textContent = v; });
    const setHtml = (sel: string, v: string) => root.querySelectorAll<HTMLElement>(sel).forEach((el) => { el.innerHTML = v; });

    // Identity — prefer the Cognito name; avoid the DB "user-N" placeholder.
    const emailName = user?.email ? (user.email.split('@')[0] ?? '') : '';
    const rawName = (stats?.name ?? '').trim();
    const name = emailName && (!rawName || /^user[\s_-]?\d+$/i.test(rawName)) ? emailName : (rawName || emailName || 'operator');
    const ini = initials(name);
    const pts = stats?.points ?? 0;
    const cleared = stats?.scenarios_completed ?? 0;
    const acc = Math.round((stats?.accuracy ?? 0) * 100);
    const rank = board?.entries.find((e) => e.user_id === stats?.user_id)?.rank ?? null;

    // Rank tier (real, from points)
    const ti = tierIndex(pts);
    const cur = TIERS[ti]!;
    const next = TIERS[ti + 1];
    const toNext = next ? next.min - pts : 0;
    const withinPct = next ? Math.min(100, ((pts - cur.min) / (next.min - cur.min)) * 100) : 100;

    setText('.avatar>span, .p-av>span', ini);
    root.querySelectorAll<HTMLElement>('.avatar').forEach((el) => el.setAttribute('title', name));
    setText('.p-id > b', name);
    setText('.p-id .role', `${cur.n} · ${cur.role} track`);
    setHtml('.pill.pts', `${pts.toLocaleString()} <small>pts</small>`);

    // Profile XP (tier-based) — appears in the hero profile card and the rank card.
    setHtml('.p-xp .amt', next ? `<b>${pts.toLocaleString()}</b> / ${next.min.toLocaleString()} XP` : `<b>${pts.toLocaleString()}</b> XP`);
    setText('.p-xp .lvl', next ? `${cur.n} · ${toNext.toLocaleString()} XP to ${next.n}` : `${cur.n} · max rank`);
    root.querySelectorAll<HTMLElement>('.p-xp .track > i').forEach((el) => { el.style.width = `${withinPct}%`; });

    const metrics = root.querySelectorAll<HTMLElement>('.profile .p-metric');
    const setMetric = (list: NodeListOf<HTMLElement>, i: number, v: string, l?: string) => {
      const m = list[i]; if (!m) return;
      const vEl = m.querySelector<HTMLElement>('.v'); if (vEl) vEl.textContent = v;
      if (l) { const lEl = m.querySelector<HTMLElement>('.l'); if (lEl) lEl.textContent = l; }
    };
    setMetric(metrics, 0, String(cleared), 'Cleared');
    setMetric(metrics, 1, rank ? `#${rank}` : '—', 'Org board');
    setMetric(metrics, 2, `${acc}%`, 'Accuracy'); // was a fabricated "Streak"

    // Rank view: current-tier badge, subtitle, ladder highlight + metrics (all real).
    setHtml('.rankcard .tier', `${cur.n}<small>Tier ${ti + 1} of ${TIERS.length}</small>`);
    setText('.rankcard .sub', `${rank ? `#${rank} in org` : 'Unranked'} · ${cur.role} track`);
    root.querySelectorAll<HTMLElement>('.tiers .tier').forEach((el, i) => {
      el.classList.remove('done', 'on');
      if (i < ti) el.classList.add('done'); else if (i === ti) el.classList.add('on');
    });
    const rankMetrics = root.querySelectorAll<HTMLElement>('.rankcard .p-metric');
    setMetric(rankMetrics, 0, pts.toLocaleString(), 'Points'); // was "Streak"
    setMetric(rankMetrics, 1, String(cleared), 'Cleared');
    setMetric(rankMetrics, 2, `${acc}%`, 'Accuracy'); // was "Embers"

    // Leaderboards (console + rank) — real entries, "you" highlighted. Always
    // replaces the static sample rows (a clean empty state if the board is empty).
    const entries = board?.entries ?? [];
    if (board) {
      const rows = entries.length
        ? entries.slice(0, 8).map((e) => {
            const me = e.user_id === stats?.user_id;
            const dn = me ? name : (/^user[\s_-]?\d+$/i.test(e.name || '') ? `Operator ${e.rank}` : e.name);
            const eti = TIERS[tierIndex(e.points)]!;
            return `<div class="lb-row ${me ? 'me' : ''}"><span class="lb-rank">${e.rank}</span>`
              + `<span class="lb-av">${esc(initials(dn))}</span>`
              + `<span class="lb-nm">${esc(dn)}${me ? ' · you' : ''}<small>${eti.n} · ${e.scenarios_completed} cleared</small></span>`
              + `<span class="lb-pts">${e.points.toLocaleString()}</span></div>`;
          }).join('')
        : emptyNote('No ranked defenders yet — clear a lab to get on the board.');
      root.querySelectorAll('.lb').forEach((lb) => { lb.innerHTML = rows; });
    }

    // Medals → real badges.
    const earned = (stats?.badges ?? []).map((b) => (b.label || '').toLowerCase()).filter(Boolean);
    const medals = root.querySelectorAll<HTMLElement>('.medal');
    let anyMatch = false;
    medals.forEach((m) => {
      const bn = (m.querySelector('.bn')?.textContent ?? '').toLowerCase();
      const got = earned.some((e) => e && (bn.includes(e) || e.includes(bn)));
      m.classList.toggle('got', got); m.classList.toggle('lock', !got);
      if (got) anyMatch = true;
    });
    if (!anyMatch && earned.length) {
      medals.forEach((m, i) => { const got = i < earned.length; m.classList.toggle('got', got); m.classList.toggle('lock', !got); });
    }
    const gotCount = root.querySelectorAll('.medal.got').length;
    root.querySelectorAll<HTMLElement>('.blk .sec-head h2').forEach((h) => {
      if ((h.textContent ?? '').trim() === 'Medals') {
        const link = h.parentElement?.querySelector<HTMLElement>('.link');
        if (link) link.textContent = `${gotCount} / ${medals.length}`;
      }
    });

    // Learn view "watches" → real courses, rendered one card per course (no fixed
    // slots), each tagged with its slug so a click opens /courses/$id.
    const courseList = courses ?? [];
    if (courses) {
      const learnGrid = root.querySelector<HTMLElement>('[data-view="learn"] .grid-w');
      if (learnGrid) {
        learnGrid.innerHTML = courseList.length
          ? courseList.map((c, i) => courseCardHtml(c, i)).join('')
          : emptyNote('No courses yet — once your instructor enrolls your cohort, your courses appear here.');
      }
    }

    // The trainee's real lab set: their cohort assignments (the same gate the
    // default /labs page uses), enriched from the catalog. Falls back to the
    // catalog for staff previewing the template.
    const catalog = labs ?? [];
    const bySlug = new Map(catalog.map((l) => [l.slug, l]));
    const labPoints = (p?: number, taskCount?: number) => p || (taskCount ? taskCount * 40 : 0) || 120;
    const fromAssignments: CitLab[] = (assignments ?? []).map((a) => {
      const c = bySlug.get(a.lab_slug);
      return {
        slug: a.lab_slug,
        title: c?.title || titleize(a.lab_name || a.lab_slug),
        category: titleize(c?.category || c?.scenarioSlug || 'Lab'),
        difficulty: c?.difficulty,
        points: labPoints(c?.points, c?.taskCount),
      };
    });
    const fromCatalog: CitLab[] = catalog.map((l) => ({
      slug: l.slug, title: l.title, category: titleize(l.category || l.scenarioSlug || 'Lab'),
      difficulty: l.difficulty, points: labPoints(l.points, l.taskCount),
    }));
    const myLabs = fromAssignments.length ? fromAssignments : fromCatalog;
    const prog: Record<string, CitProg> = (labProg ?? {}) as Record<string, CitProg>;

    // Range boards → real labs, one card per lab (no fixed slots), each tagged
    // with its slug so a click opens the real lab page. Dashboard "Your posts"
    // shows the first few; the Labs board shows them all.
    const emptyLabs = 'No labs assigned yet — your instructor will assign your first lab and it appears here.';
    if (labs || assignments) {
      const consoleGrid = root.querySelector<HTMLElement>('[data-view="console"] .grid-r');
      if (consoleGrid) {
        const posts = myLabs.slice(0, 6);
        consoleGrid.innerHTML = posts.length ? posts.map((l, i) => labCardHtml(l, i, prog[l.slug])).join('') : emptyNote(emptyLabs);
      }
      const rangeGrid = root.querySelector<HTMLElement>('[data-view="range"] .grid-r');
      if (rangeGrid) {
        rangeGrid.innerHTML = myLabs.length ? myLabs.map((l, i) => labCardHtml(l, i, prog[l.slug])).join('') : emptyNote(emptyLabs);
      }
    }

    // Operations track → the trainee's real lab progression. Beacons are lit by
    // completed labs; the current beacon is the first unfinished one. This drives
    // BOTH the labels/footer here AND the 3D campaign route (via the global +
    // event below) so they can never disagree again.
    const track = myLabs.slice(0, 6);
    const N = track.length;
    const doneArr = Array.from({ length: 6 }, (_, i) => (i < N ? !!prog[track[i]!.slug]?.completed : false));
    let curIdx = -1;
    for (let i = 0; i < N; i++) { if (!doneArr[i]) { curIdx = i; break; } }
    const lit = doneArr.filter(Boolean).length;
    const st3d = Array.from({ length: 6 }, (_, i) => (i >= N ? 'lock' : doneArr[i] ? 'done' : i === curIdx ? 'cur' : 'lock'));
    const cur3d = curIdx >= 0 ? curIdx : N;

    root.querySelectorAll<HTMLElement>('.tk-label').forEach((el, i) => {
      const lab = track[i];
      if (!lab) { el.style.display = 'none'; el.removeAttribute('data-lab-slug'); return; }
      el.style.display = '';
      el.setAttribute('data-lab-slug', lab.slug);
      const done = doneArr[i]; const isCur = i === curIdx;
      el.classList.toggle('done', !!done); el.classList.toggle('cur', isCur && !done);
      el.innerHTML = `${esc(lab.title)}<br><span class="st">${done ? 'Lit' : isCur ? 'You are here' : 'Unlit'}</span>`;
    });
    const curLab = curIdx >= 0 ? track[curIdx] : (track[0] ?? myLabs[0]);
    if (N > 0) {
      const nextLab = curIdx >= 0 ? track[curIdx] : undefined;
      setHtml('.trk-foot .t', `<b>${lit} of ${N} beacons lit</b> ${nextLab ? `Clear ${esc(nextLab.title)} to light the next beacon.` : 'Every beacon lit — the watch holds.'}`);
    } else {
      setHtml('.trk-foot .t', `<b>No watches assigned yet</b> Your instructor will assign your first lab — it'll light the first beacon here.`);
    }
    // Push real progression to the 3D campaign map.
    (window as unknown as { __CITADEL_TRACK__?: unknown }).__CITADEL_TRACK__ = { cur: cur3d, st: st3d };
    window.dispatchEvent(new CustomEvent('citadeltrack'));

    // Remember the current lab/course for the hero CTAs + track "Continue" button.
    const curCourse = courseList.find((c) => c.enrolled) ?? courseList[0];
    if (curLab) root.dataset.curLab = curLab.slug; else delete root.dataset.curLab;
    if (curCourse) root.dataset.curCourse = curCourse.slug; else delete root.dataset.curCourse;

    // Hero — real welcome + real "continue" target.
    setText('.hero .eyebrow', `Welcome back, ${name} · Watch ${Math.min(lit + 1, N || 1)} of ${N || 6}`);
    if (curLab) {
      setHtml('.hero .h1', `Stand the watch. <em>${esc(curLab.title)} is waiting.</em>`);
      setText('.hero .lede', `Pick up ${curLab.title} where you left off, or open the labs board to take on another live-fire lab. Every clear is graded and goes on your record.`);
    } else {
      setHtml('.hero .h1', `Stand the watch. <em>Your posts are being prepared.</em>`);
      setText('.hero .lede', `Once your instructor assigns a lab it appears here and on the labs board — real attacks on isolated infrastructure, graded and logged to your record.`);
    }
    const resumeBtn = root.querySelector<HTMLElement>('.hero .h-cta .btn.primary');
    if (resumeBtn && resumeBtn.firstChild) resumeBtn.firstChild.textContent = curLab ? `Resume ${curLab.title} ` : 'Open the labs board ';

    // "Doors" — real counts.
    const enrolledCount = courseList.filter((c) => c.enrolled).length;
    const assignedOpen = myLabs.filter((l) => !prog[l.slug]?.completed).length;
    setHtml('.door[data-go="learn"] h3', `${courseList.length} courses<small>${enrolledCount} in progress</small>`);
    setHtml('.door[data-go="range"] h3', `${myLabs.length} labs<small>${assignedOpen} to clear</small>`);
    setHtml('.door[data-go="rank"] h3', `Org leaderboard<small>${rank ? `#${rank} in org` : 'Unranked'}</small>`);

    // Recommended-lab card (the design's "daily") → the trainee's current lab.
    if (curLab) {
      const daily = root.querySelector<HTMLElement>('.daily');
      if (daily) {
        daily.setAttribute('data-lab-slug', curLab.slug);
        const eb = daily.querySelector<HTMLElement>('.eyebrow'); if (eb) eb.textContent = 'Recommended next';
        const dh = daily.querySelector<HTMLElement>('h3'); if (dh) dh.textContent = curLab.title;
        const dp = daily.querySelector<HTMLElement>('p'); if (dp) dp.textContent = `${curLab.category} · a live-fire lab on isolated infrastructure. Open it, investigate and submit your findings for grading.`;
        const rw = daily.querySelector<HTMLElement>('.rw'); if (rw) rw.textContent = `Reward: +${curLab.points.toLocaleString()} XP on your record`;
        const db = daily.querySelector<HTMLElement>('.btn'); if (db) db.textContent = 'Open the lab';
      }
    }

    // Stat strip — real numbers matched by label.
    root.querySelectorAll<HTMLElement>('.stat').forEach((s) => {
      const label = (s.querySelector('span')?.textContent ?? '').toLowerCase();
      const b = s.querySelector<HTMLElement>('b'); if (!b) return;
      if (/live ranges/.test(label)) b.textContent = catalog.length.toLocaleString();
      else if (/courses/.test(label)) b.textContent = courseList.length.toLocaleString();
      else if (/defenders/.test(label)) b.textContent = (board?.entries.length ?? 0).toLocaleString();
      else if (/your points/.test(label)) b.textContent = pts.toLocaleString();
    });

    // Ticker — built from real board + catalog, not invented news.
    const tick: string[] = [];
    const top = entries[0];
    if (top) { const dn = /^user[\s_-]?\d+$/i.test(top.name || '') ? `Operator ${top.rank}` : top.name; tick.push(`<i>${esc(dn)}</i> leads the org board · <em>${top.points.toLocaleString()} pts</em>`); }
    if (rank) tick.push(`You're <em>#${rank}</em> in the org · <i>${cur.n}</i>`);
    if (curLab) tick.push(`Continue <em>${esc(curLab.title)}</em> to light your next beacon`);
    courseList.slice(0, 2).forEach((c) => tick.push(`Course: <em>${esc(c.title)}</em> · ${c.moduleCount ?? 0} modules`));
    myLabs.slice(0, 2).forEach((l) => tick.push(`Live lab: <em>${esc(l.title)}</em>`));
    tick.push(`${cleared} labs cleared · ${acc}% accuracy on your record`);
    const tk = root.querySelector('#ticker');
    if (tk && tick.length) { const html = tick.map((s) => `<span>${s}</span>`).join(''); tk.innerHTML = html + html; }

    // Paint 3D prop icons onto the cards we just built (if the module has already
    // rendered them; otherwise the 'citadelprops' listener does it when ready).
    applyCitadelProps(root);
  }, [stats, board, courses, labs, labProg, assignments, user]);

  return (
    <div ref={rootRef} className="citadel-root">
      {/* .citadel-root re-establishes the design's base text styles (the reference
          sets them on <body>) so the app's global styles can't bleed in. */}
      <style>{`.citadel-root{position:fixed;inset:0;z-index:60;overflow-x:hidden;overflow-y:auto;background:var(--night);color:var(--txt);font-family:var(--body);font-size:14.5px;line-height:1.55;-webkit-font-smoothing:antialiased}\n${CITADEL_CSS}`}</style>
      <div dangerouslySetInnerHTML={{ __html: CITADEL_HTML }} />
    </div>
  );
}
